<template>
  <modal
    :header="true"
    :footer="false"
    disable-click-outside
    class="google-connect-modal"
    data-test="graphics-googleConnectModal"
    @close="onCancel"
  >
    <template #modal-header>
      <div>
        {{ title }}
      </div>

      <transition :name="transitionName">
        <div
          v-if="step !== steps.typeSelection && step !== steps.elementCreation"
          class="google-connect-progress"
        >
          <div
            v-for="s in progressSteps"
            :key="s.label"
            class="google-connect-progress__step"
            :class="
              s.isCurrent ? 'google-connect-progress__step--current' : ''
            "
          >
            <div class="google-connect-progress__bar" />
            <div class="google-connect-progress__label">
              {{ s.label }}
            </div>
          </div>
        </div>
      </transition>

      <button
        class="modal-close"
        data-test="graphics-googleConnectModal-close"
        @click="onCancel"
      >
        <span class="fal fa-times" />
      </button>
    </template>

    <template #modal-body>
      <transition :name="transitionName">
        <!-- Type Selection -->
        <form
          v-if="step === steps.typeSelection"
          key="typeSelection"
          class="google-connect-form form"
          data-test="graphics-googleConnectTypeSelectionForm"
        >
          <div
            class="google-connect-form__card"
            data-test="graphics-googleConnectPrivate"
            data-entity="graphics-googleConnectPrivate"
            @click="selectType('private')"
          >
            <div class="google-connect-form__card-header">
              Private Sheet
            </div>

            <div class="google-connect-form__card-subheader">
              Pull data from privately owned Google Sheet. Requires
              authentication.
            </div>

            <span class="fas fa-arrow-right" />
          </div>

          <div
            class="google-connect-form__card"
            data-test="graphics-googleConnectPublic"
            data-entity="graphics-googleConnectPublic"
            @click="selectType('public')"
          >
            <div class="google-connect-form__card-header">
              Public Sheet
            </div>

            <div class="google-connect-form__card-subheader">
              Pull data from publicly available Google Sheet via URL
            </div>

            <span class="fas fa-arrow-right" />
          </div>
        </form>
        <!-- End Type Selection -->

        <!-- Public URL Form -->
        <form
          v-else-if="type === 'public' && step === steps.public.publicUrlInput"
          key="publicUrlInput"
          class="google-connect-form form"
          data-test="graphics-googleConnectPublicUrlForm"
        >
          <div class="google-connect-form__controls">
            <label>
              Sheet URL
              <input
                v-model="url"
                class="mx-input"
                placeholder="https://docs.google.com/spreadsheets/..."
                data-test="graphics-googleConnectPublicUrlInput"
              >
            </label>
          </div>

          <div class="flex flex-end">
            <button
              class="btn btn-link margin-right-sm"
              :disabled="saving"
              type="button"
              data-test="graphics-googleConnectPublicUrl-back"
              data-entity="graphics-googleConnectPublicUrl-back"
              @click.prevent="goBack"
            >
              Back
            </button>

            <div class="flex-grow margin-right-sm">
              <div
                v-if="error"
                class="text-danger text-right font-weight-bold"
              >
                {{ error }}
              </div>
            </div>

            <button
              type="submit"
              class="btn btn-style-a font-weight-bold"
              :disabled="!canSaveSheetSettings"
              data-test="graphics-googleConnectPublicUrl-next"
              data-entity="graphics-googleConnectPublicUrl-next"
              @click.prevent="loadWorkbook"
            >
              <span v-if="!saving">Next</span>
              <span v-else>Loading...</span>
            </button>
          </div>
        </form>
        <!-- End Public URL Form -->

        <!-- Public Sheet Selection Form -->
        <form
          v-else-if="type === 'public' && step === steps.public.sheetSelection"
          key="sheetSelection"
          class="google-connect-form form"
          data-test="graphics-googleConnectPublicSheetForm"
        >
          <div class="google-connect-form__controls">
            <label>
              Sheet
              <select
                v-model="sheet"
                data-test="graphics-selectPublicSheet"
                data-entity="graphics-selectPublicSheet"
              >
                <option
                  v-for="sheet in sheets"
                  :key="sheet"
                  :value="sheet"
                  :data-test="`graphics-publicSheetOption-${sheet}`"
                >
                  {{ sheet }}
                </option>
              </select>
            </label>
          </div>

          <div class="flex">
            <button
              class="btn btn-link margin-right-sm"
              :disabled="saving"
              type="button"
              data-test="graphics-googleConnectPublicSheet-back"
              data-entity="graphics-googleConnectPublicSheet-back"
              @click.prevent="goBack"
            >
              Back
            </button>

            <div class="flex-grow margin-right-sm">
              <div
                v-if="error"
                class="text-danger text-right font-weight-bold"
                data-test="graphics-googleConnectPublicSheet-error"
              >
                {{ error }}
              </div>
            </div>

            <button
              type="submit"
              class="btn btn-style-a font-weight-bold"
              @click.prevent="selectSheet"
              data-test="graphics-googleConnectPublicSheet-connect"
              data-entity="graphics-googleConnectPublicSheet-connect"
            >
              <span v-if="!saving">Connect</span>
              <span v-else>Saving...</span>
            </button>
          </div>
        </form>
        <!-- End Public Sheet Selection Form -->

        <!-- Private Workbook Selection -->
        <div
          v-else-if="type === 'private' && step === steps.private.workbookSelection"
          key="workbookSelection"
          class="google-connect-form form"
        >
          <drive-picker
            v-if="!loading && oauthToken"
            :oauth-token="oauthToken"
            :app-id="googleAppId"
            :origin="origin"
            @picker:canceled="onCancelPicker"
            @picker:picked="onPickFile"
          >
            <drive-picker-docs-view view-id="SPREADSHEETS" />
          </drive-picker>
        </div>
        <!-- End Private Workbook Selection -->

        <!-- Private Sheet Selection -->
        <form
          v-else-if="type === 'private' && step === steps.private.sheetSelection"
          key="privateSheetSelection"
          class="google-connect-form form"
          data-test="graphics-googleConnectPrivateSheetForm"
        >
          <div class="google-connect-form__controls">
            <label>
              Sheet
              <select
                v-model="sheet"
                data-test="graphics-selectPrivateSheet"
              >
                <option
                  v-for="sh in selectedWorkbook.sheets"
                  :key="sh.sheetId"
                  :value="sh.title"
                  :data-test="`graphics-privateSheetOption-${sh.sheetId}`"
                >
                  {{ sh.title }}
                </option>
              </select>
            </label>
          </div>

          <div class="flex">
            <button
              class="btn btn-link margin-right-sm"
              :disabled="saving"
              type="button"
              data-test="graphics-googleConnectPrivateSheet-back"
              data-entity="graphics-googleConnectPrivateSheet-back"
              @click.prevent="goBack"
            >
              Back
            </button>

            <div class="flex-grow margin-right-sm">
              <div
                v-if="error"
                class="text-danger text-right font-weight-bold"
                data-test="graphics-googleConnectPrivateSheet-error"
              >
                {{ error }}
              </div>
            </div>

            <button
              type="submit"
              class="btn btn-style-a font-weight-bold"
              data-test="graphics-googleConnectPrivateSheet-connect"
              data-entity="graphics-googleConnectPrivateSheet-connect"
              @click.prevent="selectPrivateSheet"
            >
              <span v-if="!saving">Connect</span>
              <span v-else>Saving...</span>
            </button>
          </div>
        </form>
        <!-- End Private Sheet Selection -->

        <!-- Element Creation -->
        <form
          v-else-if="step === steps.elementCreation"
          key="elementCreation"
          class="google-connect-form form"
          data-test="graphics-googleConnectElementCreationForm"
        >
          <div
            v-if="isLoadingData"
            class="loader"
            data-test="graphics-googleConnectElementCreationLoading"
          >
            <span class="fa-solid fa-spinner fa-spin" />
          </div>

          <template v-else>
            <div
              v-if="!sheetsData || !sheetsData.length || sheetsData[0]==[]"
              class="google-connect-form__subtitle error margin-bottom-md"
              data-test="graphics-googleConnectElementCreationNoData"
            >
              Oops! Your Google Sheet doesn't have any data. Add data before connecting it here.
            </div>
            <div
              v-else
              class="google-connect-form__wrapper"
              data-test="graphics-googleConnectElementCreationWrapper"
            >
              <div
                class="google-connect-form__subtitle margin-bottom-md">
                Get started by mapping your data to elements.
              </div>

              <div class="google-connect-form__controls google-connect-form__scrollable">
                <div class="google-connect-form__control">
                  <div class="google-connect-form__header font-weight-bold">Column</div>

                  <div class="google-connect-form__header font-weight-bold">Type</div>
                </div>

                <div
                  v-for="(col, index) in dataColumns"
                  :key="index + col"
                  class="google-connect-form__control"
                >
                  <label>
                    <input
                      v-model="selectedColumns[index]"
                      type="checkbox"
                      data-test="graphics-googleConnectElementCreation-selectColumn"
                      data-entity="graphics-googleConnectElementCreation-selectColumn"
                      @change="onToggleColumn(index)"
                    />

                    <div class="google-connect-form__column-label" :title="col">
                      <span>
                        {{ $filters.sheetColumn(index) }}
                      </span>
                      <span>
                        {{ col }}
                      </span>
                    </div>
                  </label>
                  <select
                    v-model="columnTypes[index]"
                    :disabled="!selectedColumns[index]"
                    data-test="graphics-googleConnectElementCreation-selectElementType"
                  >
                    <option value="text">Text</option>
                    <option value="image">Image</option>
                  </select>
                </div>
              </div>
            </div>

            <div class="google-connect-form__footer flex">
              <button
                class="btn btn-link margin-right-sm"
                :disabled="saving"
                type="button"
                data-test="graphics-googleConnectElementCreation-back"
                data-entity="graphics-googleConnectElementCreation-back"
                @click.prevent="goBack"
              >
                Back
              </button>

              <div class="flex-grow margin-right-sm">
                <div
                  v-if="error"
                  class="text-danger text-right font-weight-bold"
                  data-test="graphics-googleConnectElementCreation-error"
                >
                  {{ error }}
                </div>
              </div>

              <button
                class="btn btn-link margin-right-sm"
                :disabled="saving"
                type="button"
                data-test="graphics-googleConnectElementCreation-skip"
                data-entity="graphics-googleConnectElementCreation-skip"
                @click.prevent="skip"
              >
                Skip
              </button>

              <button
                type="submit"
                class="btn btn-style-a font-weight-bold"
                :disabled="!selectedColumns.filter(Boolean).length || sheetsData?.length === 0"
                data-test="graphics-googleConnectElementCreation-mapData"
                data-entity="graphics-googleConnectElementCreation-mapData"
                @click.prevent="addElements"
              >
                <span v-if="!saving">Map Data</span>
              </button>
            </div>
          </template>
        </form>
        <!-- End Element Creation -->
      </transition>
    </template>
  </modal>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import { uniqBy } from 'lodash-es';

import Modal from './Modal.vue';

import settingsMixin from '../../mixins/settings-mixins';

import catchify from 'catchify';

const SlideTransitionName = 'modal-form__slide';

// Constants to use to navigate through the different forms
const Steps = {
  typeSelection: 0,

  private: {
    workbookSelection: 2,
    sheetSelection: 3,
  },

  public: {
    publicUrlInput: 1,
    sheetSelection: 2,
  },

  elementCreation: 4,
};

export default {
  emits: ['close'],
  components: { Modal },
  mixins: [settingsMixin],
  props: {
    // The Data Provider element that contains connection metadata
    element: { type: Object, required: true },
    canvas: { type: Object, required: true },
  },

  data() {
    return {
      steps: Steps,
      saving: false,
      loading: false,
      error: null,
      transitionName: SlideTransitionName,
      pageToken: null,
      selectedAccount: null,

      // What step the user is on (in the flow)
      step: Steps.typeSelection,

      // Default type to private (via account auth)
      // public == public URL, no auth needed
      type: '',

      // ID of the selected workbook
      workbookId: null,

      sheetIndex: 0, // Backup way to get the sheet

      // Initial column-to-element mappings
      selectedColumns: [],
      columnTypes: [],
      selectedWorkbook: null,
    };
  },

  computed: {
    ...mapState({
      oauthToken(state) {
        return state.user?.user?.googleAccessToken || this.selectedAccount?.access_token;
      },

      sheets(state) {
        return state[this.element.id]?.sheets;
      },

      forceConnect(state) {
        return state[this.element.id]?.forceConnect;
      },

      isLoadingData(state) {
        return state[this.element.id]?.isLoading;
      },

      sheetsData(state) {
        return state[this.element.id]?.sheetsData;
      },
    }),

    origin() {
      // If in an iframe, we return the parent origin
      if (window.parent && window.parent !== window) {
        return process.env.TAGLANDIA_URL;
      }

      return `${window.location.protocol}//${window.location.host}`;
    },

    url: {
      get() {
        return this.element?.data?.url || '';
      },
      set(v) {
        this.updateOneValue('data.url', v, { blacklist: true });
      },
    },

    sheet: {
      get() {
        return this.element?.data?.sheet || '';
      },
      set(v) {
        this.updateOneValue('data.sheet', v, { blacklist: true });
      }
    },

    googleAppId() {
      return process.env.GOOGLE_APP_ID;
    },

    title() {
      const currentStep = this.progressSteps.find((step) => step.isCurrent);

      if (currentStep) {
        return currentStep.title;
      }

      if (this.step === Steps.elementCreation) {
        return 'Google Sheet Connected!';
      }

      return 'Select Type';
    },

    progressSteps() {
      if (this.type === 'public') {
        return [
          {
            title: 'Enter Public Sheet URL',
            label: 'Enter Sheet URL',
            isCurrent: this.step === Steps.public.publicUrlInput,
          },
          {
            title: 'Select a Sheet',
            label: 'Select Sheet',
            isCurrent: this.step === Steps.public.sheetSelection,
          },
        ];
      }

      // Private steps
      return [
        {
          title: 'Select a Spreadsheet',
          label: 'Select Spreadsheet',
          isCurrent: this.step === Steps.private.workbookSelection,
        },
        {
          title: 'Select a Sheet',
          label: 'Select Sheet',
          isCurrent: this.step === Steps.private.sheetSelection,
        },
      ];
    },

    canSaveSheetSettings() {
      return !!(this.url.trim() && !this.saving);
    },

    dataColumns() {
      const data = this.$store.state[this.element.id]?.sheetsData;
      if (!data || !data.length) {
        return [];
      }

      // TODO: This will need to be updated if we support row indices
      const cols = Math.max(...data.map((r) => r.length));

      const getColumnName = (val) => (val ? val.text || val.img || '(Blank Header)' : '(Blank Header)');

      // Just use the first row to signify columns
      return Array.from({ length: cols }).map((v, i) => getColumnName(data[0][i]));
    },

    previewRows() {
      const data = this.$store.state[this.element.id]?.sheetsData;
      if (!data || !data.length) {
        return [];
      }

      const idx = parseInt(this.columnToMap, 10);

      // Just slap 100 preview rows in there. It can be scrollable anyway
      const rows = data.slice(0, 100).map((row) => {
        if (typeof row[idx]?.text !== 'undefined') {
          return row[idx].text || '';
        }

        if (typeof row[idx]?.img !== 'undefined') {
          return '<image>';
        }

        return '<empty>';
      });
      return rows;
    },
  },

  watch: {
    sheetsData(val, oldVal) {
      // We can check length here,
      // because when toggled off the values will be false
      if (val !== oldVal) {
        for (let i = 0; i < 3; i += 1) {
          this.selectedColumns[i] = true;
          this.onToggleColumn(i);
        }
      }
    },
  },

  methods: {
    ...mapActions('graphicsTemplate', [
      'setOneElementValueByProp',
      'setManyElementValuesByProp',
      'removeElementAndMappings',
      'addNewElement',
      'setElementGroupMapById',
      'onMoveOrResize',
      'takeHistorySnapshot',
    ]),

    ...mapActions({
      getPublicSheetData(dispatch, payload) {
        return dispatch(`${this.element.id}/getPublicSheetData`, payload);
      },

      getPrivateWorkbookSheets(dispatch, payload) {
        return dispatch(`${this.element.id}/getPrivateWorkbookSheets`, payload);
      },

      getPrivateSheetData(dispatch, payload) {
        return dispatch(`${this.element.id}/getPrivateSheetData`, payload);
      },

      setForceConnect(dispatch) {
        return dispatch(`${this.element.id}/setForceConnect`);
      },

      updateSheets(dispatch, sheets) {
        return dispatch(`${this.element.id}/updateSheets`, { sheets });
      },
      updateSheetsDetails(dispatch, payload) {
        return dispatch(`${this.element.id}/updateSheetsDetails`, payload);
      },
    }),

    onCancelPicker() {
      this.step = Steps.typeSelection;
    },

    onPickFile(e) {
      const [doc] = e?.detail?.docs || [];
      this.selectWorkbook(doc);
    },

    // TODO: move these to element mixin and get consistency elsewhere?
    updateOneValue(propKey, propValue, options = {}) {
      this.setOneElementValueByProp({
        value: {
          id: this.element.id,
          propKey,
          propValue,
        },
        options,
      });
    },

    updateMultipleValues(pathValueMap, options = {}) {
      this.setManyElementValuesByProp({
        value: {
          id: this.element.id,
          pathValueMap,
        },
        options,
      });
    },

    async addElement(type, elOptions, options = {}) {
      const { settings, parentId, position = undefined } = elOptions;
      return this.addNewElement({
        value: {
          type,
          parentId,
          settings,
          position,
        },
        options,
      });
    },

    onToggleColumn(index) {
      if (!this.columnTypes[index]) {
        const data = this.$store.state?.[this.element.id]?.sheetsData;
        if (!data || !data.length) {
          this.columnTypes[index] = 'text';
          return; // No data so we can't determine type, jump to error on data.slice
        }

        const isImageColumn = (col) => {
          const columnData = data.slice(0, 100).map((row) => row[col]);

          // Not sure what the best way to be magical is...
          return !!columnData.find((val) => val && val.img);
        };

        this.columnTypes[index] = isImageColumn(index) ? 'image' : 'text';
      }
    },

    goBack() {
      this.transitionName = `${SlideTransitionName}--reverse`;

      if (this.step === Steps.elementCreation) {
        // This finite state machine might be getting crazy...
        this.step = this.type === 'public'
          ? Steps.public.sheetSelection
          : Steps.private.sheetSelection;
        this.selectedColumns = [];
        this.columnTypes = [];
      } else {
        this.step = Math.max(0, this.step - 1);
      }

      clearTimeout(this.transitionTimeout);
      this.transitionTimeout = setTimeout(() => {
        this.resetTransitionName();
      }, 500);
    },

    resetTransitionName() {
      this.transitionName = SlideTransitionName;
    },

    selectType(type) {
      this.resetTransitionName();
      this.type = type;

      if (this.type === 'public') {
        this.step = Steps.public.publicUrlInput;
      } else {
        this.step = Steps.private.workbookSelection;
        this.loadAccounts();
      }
    },

    async loadAccounts() {
      this.loading = true;
      this.setError(null);

      try {
        const { user } = this.$store.state.user;

        const resp = await fetch(
          `${process.env.USER_API}/google/accounts/${user.id}`,
          {
            credentials: 'include',
            mode: 'cors',
          },
        );

        const accounts = await resp.json();

        // Default to first,
        // since user should really only have one google account connected for now.
        this.selectedAccount = accounts[0] || null;

        if (!this.selectedAccount) {
          this.addNewAccount();
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.log('Error loading google accounts:', err);
        this.setError('Something went wrong :(');
      } finally {
        this.loading = false;
      }
    },

    /**
     * Open a new window to connect a Google Account
     * When the window is closed, refetch accounts
     */
    addNewAccount() {
      const { user } = this.$store.state.user;
      const features = 'popup=true,width=600,height=600';
      window.open(
        `${process.env.USER_API}/google/oauth/${user.id}?redirect=graphics`,
        '_blank',
        features,
      );
      window.addEventListener('focus', this.onFocus);
    },

    onFocus() {
      window.removeEventListener('focus', this.onFocus);
      this.loadAccounts();
    },

    async selectWorkbook(workbook) {
      // We already have the list of sheets to choose from,
      // so just go to the next step
      this.selectedWorkbook = workbook;
      this.workbookId = workbook.id;
      this.step = Steps.private.sheetSelection;

      const { sheets } = await this.getPrivateWorkbookSheets({
        workbookId: this.selectedWorkbook.id,
        accountId: this.selectedAccount.id,
      });

      this.selectedWorkbook.sheets = sheets || [];

      const filteredSheets = sheets.filter((sh) => sh?.title);

      // Save sheets in store
      this.updateSheets(
        filteredSheets.map((sh) => sh?.title),
      );

      this.updateSheetsDetails(filteredSheets);

      // Default to first sheet
      const [sheet] = sheets;
      this.sheet = sheet?.title || '';
      this.sheetIndex = 0;

      // Store name of workbook so we can show that in sidebar
      this.updateOneValue('data.workbookName', workbook.name, { blacklist: true });
    },

    selectPrivateSheet() {
      const { user } = this.$store.state.user;

      this.saving = true;

      // update the default generic name to reflect the workbookName
      this.updateOneValue('name', this.element.data.workbookName, { blacklist: true });

      const sheetIndex = this.sheets.indexOf(this.sheet);
      const { sheetId } = this.selectedWorkbook.sheets[sheetIndex];

      this.updateMultipleValues({
        'data.isConnected': true,
        'data.type': 'private-google-sheet',
        'data.workbookId': this.selectedWorkbook.id,
        'data.accountId': this.selectedAccount.id,
        'data.sheet': this.sheet,
        'data.sheetId': sheetId,
        'data.sheetIndex': sheetIndex,
        'data.url': `https://docs.google.com/spreadsheets/d/${this.selectedWorkbook.id}/edit`,
        'data.owner.id': user?._id,
        'data.owner.displayName': user?.profile?.displayName,
      }, { blacklist: true });

      const workbookId = this.selectedWorkbook.id;
      const accountId = this.selectedAccount.id;

      // Dummy range to try to grab "everything"
      this.getPrivateSheetData({
        workbookId,
        accountId,
        sheetId,
        cache: false,
      });

      this.now = Date.now();
      this.updateOneValue('data.lastSync', this.now, { blacklist: true });

      this.saving = false;
      this.finish();
    },

    setError(error) {
      this.error = error?.message || error;
    },

    /**
     * Fetch public workbook by provided URL
     */
    async loadWorkbook() {
      this.saving = true;
      this.setError(null);

      const url = this.url.trim();
      this.updateOneValue('data.url', url, { blacklist: true });

      const [err] = await catchify(
        this.getPublicSheetData({
          key: this.element.id,
          url,
        }),
      );

      if (err) {
        this.setError(err);
        this.saving = false;
        return;
      }

      // Set default sheet
      this.sheet = this.sheets[0]; // eslint-disable-line prefer-destructuring
      this.saving = false;
      this.step = Steps.public.sheetSelection;
    },

    async selectSheet() {
      const { url } = this.element.data;

      this.saving = true;

      // update the default generic name to reflect the workbookName
      this.updateOneValue('name', this.sheet, { blacklist: true });

      this.getPublicSheetData({
        key: this.element.id,
        url,
        sheet: this.sheet,
      });

      this.now = Date.now();

      this.updateMultipleValues({
        'data.isConnected': true,
        'data.type': 'public-google-sheet',
        'data.sheet': this.sheet,
        'data.sheetIndex': this.sheets.indexOf(this.sheet),
        'data.workbookName': this.sheet, // We don't know the workbook name, so just use the sheet name
        'data.lastSync': this.now,
      }, { blacklist: true });
      this.saving = false;

      this.finish();
    },

    /**
     * Wrapper around close() that will also remove the sheet-data element if it was just added
     */
    onCancel() {
      if (this.forceConnect && this.step !== Steps.elementCreation) {
        this.removeElementAndMappings({
          value: {
            element: this.element,
          },
          options: {
            blacklist: true,
          },
        });
      }

      this.close();
    },

    /**
     * If this is the first time connecting a sheet,
     *  transition to new page to create a text element.
     * Otherwise, just close the modal.
     */
    finish() {
      if (!this.forceConnect) {
        this.takeHistorySnapshot();
        this.close();
        return;
      }

      this.step = Steps.elementCreation;
    },

    async skip() {
      await this.addDataList();
      this.close();
    },

    async addDataList() {
      const dataListEl = await this.addElement('data-list', {
        parentId: this.element.id,
        settings: {
          name: 'Dynamic List',
          type: 'data-list',
          data: {
            range: '',
            filters: [
              {
                type: 'pagination',
                offset: 1, // Start from item... (index + 1)
                page: 1, // Page number (index + 1)
                limit: 10, // Items per page
              },
            ],
          },
          settings: {
            gap: 0,
            layout: 'column',
          },
        },
      }, { blacklist: true });
      this.setElementGroupMapById({
        value: {
          id: dataListEl.id,
          childIds: [],
        },
        options: {
          blacklist: true,
        },
      });

      return dataListEl;
    },

    /**
     * Add new elements with data mapping to current sheet's data-list child.
     * Then close the modal.
     */
    async addElements() {
      const dataListEl = await this.addDataList();

      // Magic numbers
      const defaultHeight = 50;
      let defaultWidth = 150;
      const defaultPadding = 10;

      const colCount = this.selectedColumns.filter(Boolean).length;

      // Scale elements down to fit size of canvas (includes addition of padding)
      defaultWidth = Math.round(
        (this.canvas.width - (colCount - 1) * defaultPadding) / colCount,
      );

      // ...within bounds (60px - 300px)
      defaultWidth = Math.min(Math.max(defaultWidth, 60), 300);

      let offset = 0;

      for (let i = 0; i < this.selectedColumns.length; i += 1) {
        if (this.selectedColumns[i] && this.dataColumns[i]) {
          const type = this.columnTypes[i] || 'text';
          let mappedProperty;
          const elementType = type;

          switch (type) {
            case 'image':
              mappedProperty = 'imageUrl';
              break;
            default:
              mappedProperty = 'text';
              break;
          }

          // Pile up elements at the right side if the extend too far
          offset = Math.min(offset, this.canvas.width - defaultWidth);

          const settings = {
            data: {
              provider: this.element.id,
              dataKeyMap: {
                [mappedProperty]: i,
              },
            },

            styles: {
              top: 0,
              left: offset,
              width: defaultWidth,
            },
          };

          if (type === 'image') {
            // TODO: Get height of data-list child
            settings.styles.height = defaultHeight;
            settings.styles.width = defaultWidth;
          }

          // Default name to the column header
          const data = this.$store.state[this.element.id]?.sheetsData;
          if (data && data.length) {
            const columnHeader = data[0][i];
            settings.name = (columnHeader && columnHeader.text) || settings.name;
          }

          this.addElement(elementType, {
            parentId: dataListEl.id,
            settings,
            position: 'end',
          });

          // Push elements over
          offset += settings.styles.width + defaultPadding;
        }
      }

      this.takeHistorySnapshot();
      this.close();
    },

    close() {
      // Make sure modal isn't forcefully opened again
      if (this.forceConnect) {
        this.setForceConnect(false);
      }

      this.$emit('close');

      // Reset modal by to type selection
      this.step = Steps.typeSelection;
    },
  },
};
</script>
